import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import {
  ActionPerformed,
  PushNotificationSchema,
  PushNotifications,
  Token,
} from '@capacitor/push-notifications';
import { DateInterval } from 'src/app/model/date-interval.model';
import { Observable } from 'rxjs';
import { createAuthorizationHeader } from 'src/app/utils/headers';
import { GlobalService } from 'src/app/global.config';
import { HttpClient } from '@angular/common/http';
import { SigninService } from 'src/app/signin/signin.service';
import { TokenLutDto } from 'src/app/model/data-managemnt';
import { User } from 'src/app/signin/credentials';
import { isValidFcmToken } from 'src/app/utils/push/push-token.utils';
import { PushPlatform, WebPushService } from 'src/app/utils/push/web-push.service';

@Injectable({
  providedIn: 'root'
})
export class AlertManagementService {
  private pushListenersReady = false;
  private iosFcmListenersReady = false;
  private pushRegistering = false;
  /** Latest context used by native push registration listeners. */
  private nativePushContext: {
    deviceId: string;
    username: string;
    language: string;
    platform: PushPlatform;
    onPushReceived?: () => void;
  } | null = null;

  constructor(
    private signinService: SigninService,
    private _http: HttpClient,
    private globalService: GlobalService,
    private webPushService: WebPushService
  ) {}

  getNotifications(dateInterval: DateInterval): Observable<any> {
    let headers = createAuthorizationHeader();
    return this._http.post(
      this.globalService.dns +
        'alerts/notifications/' +
        this.signinService.isRootAdmin() +
        '/' +
        this.signinService.getUserId(),
      dateInterval,
      { headers: headers }
    );
  }

  add(tokenLutDto: TokenLutDto) {
    let headers = createAuthorizationHeader();
    return this._http.post(this.globalService.dns + 'tokenLut/update', tokenLutDto, {
      headers: headers,
    });
  }

  /**
   * Register FCM token (Android / iOS native, or browser Web Push) and upsert token_lut.
   * Call after login (when id_token + currentUser are saved).
   */
  async registerPushToken(user?: User | null, onPushReceived?: () => void): Promise<void> {
    const currentUser = user || this.readCurrentUser();
    if (!currentUser?.username) {
      console.warn('Cannot register push token: user is not signed in');
      return;
    }

    if (this.pushRegistering) {
      // Keep callback/context fresh if a register is already in flight (login + toolbar).
      if (onPushReceived && this.nativePushContext) {
        this.nativePushContext.onPushReceived = onPushReceived;
      }
      return;
    }
    this.pushRegistering = true;

    try {
      if (Capacitor.isNativePlatform()) {
        await this.registerNativePushToken(currentUser, onPushReceived);
      } else {
        await this.registerWebPushToken(currentUser, onPushReceived);
      }
    } catch (err) {
      console.error('Error requesting/registering push notifications:', err);
    } finally {
      this.pushRegistering = false;
    }
  }

  private async registerNativePushToken(
    currentUser: User,
    onPushReceived?: () => void
  ): Promise<void> {
    let deviceId = '';
    let language = localStorage.getItem('lang') || 'fr';
    const platform = this.resolveNativePlatform();

    try {
      const idInfo = await Device.getId();
      deviceId = idInfo.identifier || '';
      const langInfo = await Device.getLanguageCode();
      language = localStorage.getItem('lang') || langInfo.value || 'fr';
    } catch (error) {
      console.error('Error getting device info:', error);
    }

    if (!deviceId) {
      console.warn('Cannot register push token: unique device id is missing');
      return;
    }

    this.nativePushContext = {
      deviceId,
      username: currentUser.username,
      language,
      platform,
      onPushReceived,
    };

    if (platform === 'ios') {
      await this.ensureIosFcmListeners();
      await this.registerIosFcmToken();
      return;
    }

    await this.ensureNativePushListeners(true);

    // Small delay so the first authenticated HTTP calls (e.g. groupes/details)
    // finish before Android shows the notification permission dialog.
    await new Promise((resolve) => setTimeout(resolve, 1500));

    let permission = await PushNotifications.checkPermissions();
    if (permission.receive !== 'granted') {
      permission = await PushNotifications.requestPermissions();
    }
    if (permission.receive !== 'granted') {
      console.warn('Push notification permission not granted:', permission.receive);
      return;
    }

    console.log(`[Push] Calling register() on platform=${platform}`);
    await PushNotifications.register();
  }

  /**
   * iOS: obtain FCM token via Firebase Messaging (not the APNs hex from PushNotifications).
   * Requires GoogleService-Info.plist + AppDelegate wiring apnsToken → Firebase.
   */
  private async registerIosFcmToken(): Promise<void> {
    await new Promise((resolve) => setTimeout(resolve, 1500));

    let permission = await FirebaseMessaging.checkPermissions();
    if (permission.receive !== 'granted') {
      permission = await FirebaseMessaging.requestPermissions();
    }
    if (permission.receive !== 'granted') {
      console.warn('[Push][iOS] Notification permission not granted:', permission.receive);
      return;
    }

    const { token } = await FirebaseMessaging.getToken();
    if (!token) {
      console.error('[Push][iOS] FirebaseMessaging.getToken() returned no token');
      return;
    }

    this.publishNativeFcmToken(token, 'ios');
  }

  private publishNativeFcmToken(token: string, platform: PushPlatform): void {
    const ctx = this.nativePushContext;
    if (!ctx?.username || !ctx.deviceId) {
      console.warn('[Push] Cannot publish token: missing user/device context');
      return;
    }

    if (!isValidFcmToken(token)) {
      console.error(
        `[Push] Refusing to save token for platform=${platform} — looks like APNs, not FCM`,
        { tokenLength: token.length }
      );
      return;
    }

    console.log(
      `[Push] FCM token ready for device=${ctx.deviceId} user=${ctx.username} platform=${platform}`
    );
    console.log('[Push] token length=', token.length);

    this.publishTokenLut({
      deviceid: ctx.deviceId,
      username: ctx.username,
      token,
      language: ctx.language || 'fr',
      lastTime: new Date(),
      platform,
    });
  }

  private async ensureIosFcmListeners(): Promise<void> {
    if (this.iosFcmListenersReady) {
      return;
    }

    await FirebaseMessaging.addListener('tokenReceived', (event) => {
      if (!event?.token) {
        return;
      }
      console.log('[Push][iOS] FCM token refreshed');
      this.publishNativeFcmToken(event.token, 'ios');
    });

    await FirebaseMessaging.addListener('notificationReceived', (event) => {
      console.log('[Push][iOS] notification received:', JSON.stringify(event));
      this.nativePushContext?.onPushReceived?.();
    });

    await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
      console.log('[Push][iOS] notification action performed:', JSON.stringify(event));
    });

    this.iosFcmListenersReady = true;
  }

  private async registerWebPushToken(
    currentUser: User,
    onPushReceived?: () => void
  ): Promise<void> {
    const language = localStorage.getItem('lang') || 'fr';
    const deviceId = this.webPushService.resolveBrowserDeviceId();

    if (!this.webPushService.isConfigured()) {
      console.error(
        '[WebPush] tokenLut/update skipped: active profile has no firebase web config (vapidKey/appId). ' +
          'Add firebase: { ... } on the profile in global.config.ts.'
      );
      return;
    }

    const token = await this.webPushService.requestPermissionAndToken((payload) => {
      console.log('[WebPush] Foreground push received', payload);
      if (onPushReceived) {
        onPushReceived();
      }
    });

    if (!token) {
      console.error('[WebPush] No FCM web token obtained; tokenLut/update was not called');
      return;
    }

    console.log('[WebPush] Publishing token to tokenLut/update', { deviceId, platform: 'web' });
    this.publishTokenLut({
      deviceid: deviceId,
      username: currentUser.username,
      token,
      language,
      lastTime: new Date(),
      platform: 'web',
    });
  }

  private resolveNativePlatform(): PushPlatform {
    const platform = Capacitor.getPlatform();
    if (platform === 'ios') {
      return 'ios';
    }
    return 'android';
  }

  private readCurrentUser(): User | null {
    try {
      return JSON.parse(localStorage.getItem('currentUser') || 'null');
    } catch {
      return null;
    }
  }

  private publishTokenLut(tokenLutDto: TokenLutDto): void {
    if (!isValidFcmToken(tokenLutDto.token)) {
      console.error(
        `[Push] Refusing token_lut update for platform=${tokenLutDto.platform || 'unknown'} — invalid FCM token`
      );
      return;
    }

    this.add(tokenLutDto).subscribe({
      next: () =>
        console.log(
          `FCM token published to token_lut (platform=${tokenLutDto.platform || 'unknown'})`
        ),
      error: (err) => console.error('Error publishing FCM token to token_lut:', err),
    });
  }

  /**
   * Attach native push listeners once.
   * On Android, registration delivers the FCM token. On iOS, registration is APNs-only — use FirebaseMessaging instead.
   */
  private async ensureNativePushListeners(includeRegistration: boolean): Promise<void> {
    if (this.pushListenersReady) {
      return;
    }

    if (includeRegistration) {
      await PushNotifications.addListener('registration', (token: Token) => {
        if (!token?.value) {
          console.warn('[Push] registration event ignored: missing token');
          return;
        }
        this.publishNativeFcmToken(token.value, 'android');
      });
    }

    await PushNotifications.addListener('registrationError', (error: any) => {
      console.error('FCM registration error:', JSON.stringify(error));
    });

    await PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        console.log('Push received:', JSON.stringify(notification));
        this.nativePushContext?.onPushReceived?.();
      }
    );

    await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (notification: ActionPerformed) => {
        console.log('Push action performed:', JSON.stringify(notification));
      }
    );

    this.pushListenersReady = true;
  }

  getNotificationsByIdDeviceType(
    idDevice: number,
    type: string,
    dateInterval: DateInterval
  ): Observable<any> {
    let headers = createAuthorizationHeader();
    return this._http.post(
      this.globalService.dns + 'alerts/notificationsdetails/' + idDevice,
      { dateInterval, type },
      { headers: headers }
    );
  }

  findVehicleByDeviceId(deviceId: number) {}

  getEventsByDeviceAndType(deviceId, type) {}
}
